import { Brain, ClipboardList, Clock, HelpCircle, Play, ShieldAlert, Timer } from "lucide-react";
import { formatDateTime } from "../utils/utils";

const quizTypeConfig = {
  learning: {
    title: "Learning Quiz",
    subtitle: "Reinforce concepts with guided practice and step-by-step explanations.",
    icon: Brain,
  },
  assessment: {
    title: "Assessment Quiz",
    subtitle: "Evaluate your comprehension and test what you've learned so far.",
    icon: ClipboardList,
  },
  exam: {
    title: "Exam Quiz",
    subtitle: "Comprehensive assessment contributing to your final performance record.",
    icon: Timer,
  },
};

const NextQuizCard = ({
  quiz,
  currentTopicTitle = null,
  onStartQuiz,
  loading = false,
  className = "",
}) => {
  if (!quiz) return null;

  const config = quizTypeConfig[quiz.quiz_type] || {
    title: `${quiz.quiz_type ? quiz.quiz_type.charAt(0).toUpperCase() + quiz.quiz_type.slice(1) : "Next"} Quiz`,
    subtitle: "Continue your progression with the next available quiz.",
    icon: HelpCircle,
  };

  const Icon = config.icon;
  const isDifferentTopic =
    currentTopicTitle &&
    quiz.topic_title &&
    currentTopicTitle.trim().toLowerCase() !== quiz.topic_title.trim().toLowerCase();

  const isExam = quiz.quiz_type === "exam";
  const now = new Date();
  const isClosed = isExam && (quiz.status?.status === "closed" || (quiz.end_date && new Date(quiz.end_date) < now));
  const isUpcoming = isExam && quiz.start_date && new Date(quiz.start_date) > now;

  const getButtonContent = () => {
    if (isClosed) return "Quiz Closed";
    if (isUpcoming) return "Quiz Not Started Yet";
    return (
      <span className="flex items-center gap-2">
        <Play className="w-4 h-4 fill-current" />
        Take Next Quiz
      </span>
    );
  };

  const isButtonDisabled = isClosed || isUpcoming;

  return (
    <div
      className={`bg-white border border-gray-300 rounded-xl p-5 shadow-sm hover:border-gray-400 transition-all ${className}`}
    >
      {/* Top Header Row */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
            Up Next
          </span>
          <span className="badge badge-sm badge-outline text-xs rounded-lg opacity-80">
            {isDifferentTopic ? "Next Topic in Course" : "Next in Topic"}
          </span>
        </div>

        {quiz.course_title && (
          <span className="text-xs opacity-60 truncate max-w-xs">
            {quiz.course_title}
          </span>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center font-semibold bg-primary/10 text-primary shrink-0">
            <Icon className="w-5 h-5" />
          </div>

          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-base sm:text-lg text-base-content leading-snug">
                {config.title}
              </h3>
              <span className="badge badge-outline text-xs rounded-lg capitalize">
                {quiz.quiz_type}
              </span>
              {quiz.proctoring_enabled && (
                <span className="badge badge-warning badge-outline text-xs rounded-lg gap-1">
                  <ShieldAlert className="w-3 h-3" /> Proctored
                </span>
              )}
            </div>

            {quiz.topic_title && (
              <p className="text-sm font-medium text-base-content">
                Topic: <span className="opacity-80">{quiz.topic_title}</span>
              </p>
            )}

            <p className="text-sm opacity-70 line-clamp-2">{config.subtitle}</p>

            {/* Metadata row */}
            <div className="flex flex-wrap items-center gap-4 text-xs opacity-70 pt-1">
              {quiz.number_of_questions_to_answer > 0 && (
                <div className="flex items-center gap-1.5">
                  <HelpCircle className="w-3.5 h-3.5" />
                  <span>{quiz.number_of_questions_to_answer} Questions</span>
                </div>
              )}

              {quiz.expected_duration > 0 && (
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{quiz.expected_duration} mins</span>
                </div>
              )}

              {quiz.start_date && isExam && (
                <div className="flex items-center gap-1">
                  <span>Open: {formatDateTime(quiz.start_date)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="self-end sm:self-center shrink-0">
          <button
            onClick={() => onStartQuiz(quiz)}
            disabled={isButtonDisabled || loading}
            className="btn btn-primary"
          >
            {getButtonContent()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NextQuizCard;
